/**
 * Motor de inferencia del Genie (orientación topográfica).
 *
 * Implementa el esquema de "puntuación por peso" de idea01.md
 * (`evaluar_sospecha_inicial`): cada respuesta suma pesos a una o varias
 * categorías de sospecha y el motor devuelve la hipótesis dominante.
 *
 * NO emite diagnósticos: produce *priors* que orientan al clínico y que el
 * `DiagnosticPatternAnalyzer` puede consumir como evidencia adicional.
 */

import {
  GENIE_QUESTIONS,
  GENIE_TIEBREAKERS,
  GENIE_SECTION_SUGGESTIONS,
  GENIE_COMPOSITE_RULES,
  GENIE_WARNING_RULES,
  GENIE_DIAGNOSIS_CATALOG,
  SUSPICION_LABELS,
  PATTERN_SUSPICION_CATEGORIES,
  SuspicionCategory,
  GenieQuestion,
  GenieTiebreaker,
  GenieOrientation,
  GenieWindow,
  GenieDiagnosisCatalogEntry,
  TemporalCourse,
  UNKNOWN_SUSPICION_ID,
  CURSO_TO_TEMPORAL,
  ALL_TEMPORAL_COURSES,
  getDiagnosisName,
  getGenieQuestionsForWindow,
} from '../data/genieQuestions';
import type { DiagnosticPattern, Evidence } from '../types/diagnostic';

/** Respuestas del usuario: questionId -> optionId. */
export type GenieAnswers = Record<string, string>;

export interface SuspicionScore {
  category: SuspicionCategory;
  label: string;
  /** Puntaje crudo acumulado. */
  rawScore: number;
  /** Puntaje normalizado 0–1 respecto al total. */
  normalized: number;
}

export interface CompositeRationale {
  /** Texto principal (regla más específica que matchea). */
  summary: string;
  /** Reglas secundarias aplicables (máx. 2). */
  secondary: string[];
  /** Entradas del catálogo filtradas por curso + categoría topográfica. */
  compatibleDiagnoses: GenieDiagnosisCatalogEntry[];
  /** Pista sobre la siguiente pregunta recomendada. */
  nextStepHint?: string;
}

export interface GenieEvaluation {
  /** Sospecha dominante, o null si no hay respuestas suficientes. */
  topSuspicion: SuspicionScore | null;
  /** Todas las categorías ordenadas de mayor a menor puntaje. */
  ranking: SuspicionScore[];
  /** ¿Hay empate técnico entre las dos primeras (< 15% de diferencia)? */
  isAmbiguous: boolean;
  /** Justificaciones clínicas de las respuestas elegidas. */
  rationales: string[];
  /** Nº de preguntas respondidas / total activas en el alcance. */
  answeredCount: number;
  totalCount: number;
  /** Razonamiento compuesto acumulativo. */
  compositeRationale: CompositeRationale | null;
}

const ALL_CATEGORIES: SuspicionCategory[] = ['neuropatico', 'miopatico', 'unm', 'central'];

/** Umbral relativo por debajo del cual dos sospechas se consideran empatadas. */
const AMBIGUITY_THRESHOLD = 0.15;

/** Tras responder curso temporal, priorizar estas preguntas de síntomas. */
const CLINICAL_PRIORITY_AFTER_TEMPO = ['distribucion', 'reflejos', 'tipo_fibras'] as const;

// ---------------------------------------------------------------------------
// Visibilidad condicional de preguntas
// ---------------------------------------------------------------------------

/** ¿Las respuestas actuales cumplen TODAS las condiciones? */
export function matchesConditions(
  answers: GenieAnswers,
  when: Record<string, string[]>
): boolean {
  return Object.entries(when).every(([questionId, optionIds]) => {
    const selected = answers[questionId];
    return selected !== undefined && optionIds.includes(selected);
  });
}

/** ¿La pregunta es visible dados los answers actuales? */
export function isQuestionVisible(question: GenieQuestion, answers: GenieAnswers): boolean {
  if (question.hideWhen && matchesConditions(answers, question.hideWhen)) {
    return false;
  }
  if (question.showWhen) {
    return matchesConditions(answers, question.showWhen);
  }
  return true;
}

/** Preguntas visibles según respuestas (opcionalmente filtradas por ventana). */
export function getVisibleGenieQuestions(
  answers: GenieAnswers,
  scope?: GenieWindow
): GenieQuestion[] {
  const pool = scope ? getGenieQuestionsForWindow(scope) : GENIE_QUESTIONS;
  return pool.filter(q => isQuestionVisible(q, answers));
}

/** Alias explícito para la UI por ventana. */
export function getActiveQuestionsForWindow(
  answers: GenieAnswers,
  window: GenieWindow
): GenieQuestion[] {
  return getVisibleGenieQuestions(answers, window);
}

/** Elimina respuestas a preguntas que ya no son visibles. */
export function pruneOrphanGenieAnswers(answers: GenieAnswers): GenieAnswers {
  const visibleIds = new Set(getVisibleGenieQuestions(answers).map(q => q.id));
  const pruned: GenieAnswers = {};
  for (const [questionId, optionId] of Object.entries(answers)) {
    if (visibleIds.has(questionId)) {
      pruned[questionId] = optionId;
    }
  }
  return pruned;
}

function resolveActiveQuestions(answers: GenieAnswers, scope?: GenieWindow): GenieQuestion[] {
  return getVisibleGenieQuestions(answers, scope);
}

// ---------------------------------------------------------------------------
// Razonamiento compuesto y catálogo temporal
// ---------------------------------------------------------------------------

function getTemporalCourseFromAnswers(answers: GenieAnswers): TemporalCourse | null {
  const curso = answers['curso_temporal'];
  if (!curso) return null;
  return CURSO_TO_TEMPORAL[curso] ?? null;
}

function ruleSpecificity(rule: { when: Record<string, string[]> }): number {
  return Object.keys(rule.when).length;
}

function matchingCompositeRules(answers: GenieAnswers) {
  return GENIE_COMPOSITE_RULES.filter(rule => matchesConditions(answers, rule.when)).sort(
    (a, b) => ruleSpecificity(b) - ruleSpecificity(a)
  );
}

/** Filtra el catálogo por curso temporal y categoría topográfica dominante. */
export function getCompatibleCatalogEntries(
  answers: GenieAnswers,
  topCategory: SuspicionCategory | null,
  highlightIds?: string[]
): GenieDiagnosisCatalogEntry[] {
  const temporal = getTemporalCourseFromAnswers(answers);
  const highlightSet = highlightIds ? new Set(highlightIds) : null;

  let entries = GENIE_DIAGNOSIS_CATALOG.filter(entry => {
    const fit = entry.temporalFit ?? ALL_TEMPORAL_COURSES;
    if (temporal && !fit.includes(temporal)) return false;
    if (topCategory && entry.category !== topCategory) return false;
    return true;
  });

  if (highlightSet && highlightSet.size > 0) {
    const highlighted = entries.filter(e => highlightSet.has(e.id));
    if (highlighted.length > 0) {
      entries = highlighted;
    }
  }

  if (entries.length === 0 && topCategory) {
    entries = GENIE_DIAGNOSIS_CATALOG.filter(entry => {
      const fit = entry.temporalFit ?? ALL_TEMPORAL_COURSES;
      return (!temporal || fit.includes(temporal)) && entry.category === topCategory;
    });
  }

  if (entries.length === 0) {
    entries = GENIE_DIAGNOSIS_CATALOG.filter(entry => {
      const fit = entry.temporalFit ?? ALL_TEMPORAL_COURSES;
      return !temporal || fit.includes(temporal);
    });
  }

  return entries;
}

/** Construye el razonamiento compuesto acumulativo. */
export function buildCompositeRationale(
  answers: GenieAnswers,
  evaluation: Pick<GenieEvaluation, 'topSuspicion' | 'answeredCount'>,
  scope?: GenieWindow
): CompositeRationale | null {
  if (evaluation.answeredCount === 0) return null;

  const matched = matchingCompositeRules(answers);
  const primary = matched[0];
  const secondary = matched.slice(1, 3).map(r => r.message);

  const summary =
    primary?.message ??
    (evaluation.topSuspicion
      ? `Sospecha topográfica: ${evaluation.topSuspicion.label}. Complete más preguntas para acotar el diferencial.`
      : 'Complete distribución, tipo de fibras y reflejos para orientar la localización.');

  const compatibleDiagnoses = getCompatibleCatalogEntries(
    answers,
    evaluation.topSuspicion?.category ?? null,
    primary?.highlightCatalogIds
  );

  let nextStepHint: string | undefined;
  if (scope) {
    const nextQ = getBestNextQuestion(answers, evaluation as GenieEvaluation, scope);
    if (nextQ) {
      nextStepHint = `Siguiente: ${nextQ.text}`;
    }
  }

  return { summary, secondary, compatibleDiagnoses, nextStepHint };
}

// ---------------------------------------------------------------------------
// Evaluación principal
// ---------------------------------------------------------------------------

/**
 * Evalúa el vector de sospecha a partir de las respuestas.
 * @param answers Mapa questionId -> optionId.
 * @param scope   Limita el cálculo a una ventana (opcional).
 */
export function evaluateGenie(answers: GenieAnswers, scope?: GenieWindow): GenieEvaluation {
  const questions = resolveActiveQuestions(answers, scope);
  const scores: Record<SuspicionCategory, number> = {
    neuropatico: 0,
    miopatico: 0,
    unm: 0,
    central: 0,
  };
  const rationales: string[] = [];
  let answeredCount = 0;

  for (const question of questions) {
    const optionId = answers[question.id];
    if (!optionId) continue;

    const option = question.options.find(o => o.id === optionId);
    if (!option) continue;

    answeredCount += 1;
    for (const category of ALL_CATEGORIES) {
      scores[category] += option.weights[category] ?? 0;
    }
    if (option.rationale) {
      rationales.push(option.rationale);
    }
  }

  const total = ALL_CATEGORIES.reduce((sum, c) => sum + scores[c], 0);

  const ranking: SuspicionScore[] = ALL_CATEGORIES.map(category => ({
    category,
    label: SUSPICION_LABELS[category],
    rawScore: scores[category],
    normalized: total > 0 ? scores[category] / total : 0,
  })).sort((a, b) => b.rawScore - a.rawScore);

  const topSuspicion = answeredCount > 0 && ranking[0].rawScore > 0 ? ranking[0] : null;

  const isAmbiguous =
    topSuspicion !== null &&
    ranking.length > 1 &&
    ranking[1].rawScore > 0 &&
    ranking[0].normalized - ranking[1].normalized < AMBIGUITY_THRESHOLD;

  const partial = { topSuspicion, ranking, isAmbiguous, rationales, answeredCount, totalCount: questions.length };
  const compositeRationale = buildCompositeRationale(answers, partial, scope);

  return { ...partial, compositeRationale };
}

/**
 * Devuelve la siguiente pregunta sin responder dentro del alcance, o null si
 * ya se respondieron todas. Útil para guiar el flujo al estilo "Akinator".
 */
export function getNextGenieQuestion(
  answers: GenieAnswers,
  scope?: GenieWindow
): GenieQuestion | null {
  const questions = resolveActiveQuestions(answers, scope);
  return questions.find(q => !answers[q.id]) ?? null;
}

/**
 * Devuelve la siguiente mejor pregunta priorizando poder discriminatorio cuando
 * hay ambigüedad entre las dos primeras categorías de sospecha. Sin ambigüedad
 * devuelve simplemente la siguiente en orden.
 *
 * Tras responder curso temporal en síntomas, prioriza distribución → reflejos → fibras.
 */
export function getBestNextQuestion(
  answers: GenieAnswers,
  evaluation: GenieEvaluation,
  scope?: GenieWindow
): GenieQuestion | null {
  const questions = resolveActiveQuestions(answers, scope);
  const unanswered = questions.filter(q => !answers[q.id]);
  if (unanswered.length === 0) return null;

  if (scope === 'symptoms' && answers['curso_temporal']) {
    for (const priorityId of CLINICAL_PRIORITY_AFTER_TEMPO) {
      const priorityQ = unanswered.find(q => q.id === priorityId);
      if (priorityQ) return priorityQ;
    }
  }

  if (!evaluation.isAmbiguous || evaluation.ranking.length < 2) {
    return unanswered[0];
  }

  const cat1 = evaluation.ranking[0].category;
  const cat2 = evaluation.ranking[1].category;

  let bestQ = unanswered[0];
  let bestPower = -Infinity;

  for (const q of unanswered) {
    const power =
      q.options.length === 0
        ? 0
        : Math.max(
            ...q.options.map(opt =>
              Math.abs((opt.weights[cat1] ?? 0) - (opt.weights[cat2] ?? 0))
            )
          );
    if (power > bestPower) {
      bestPower = power;
      bestQ = q;
    }
  }

  return bestQ;
}

/**
 * Construye una frase de orientación lista para mostrar o adjuntar al reporte.
 */
export function describeGenieEvaluation(evaluation: GenieEvaluation): string {
  if (!evaluation.topSuspicion) {
    return 'Aún no hay suficientes respuestas para orientar la sospecha topográfica.';
  }

  const pct = Math.round(evaluation.topSuspicion.normalized * 100);
  const base = `Sospecha topográfica dominante: ${evaluation.topSuspicion.label} (${pct}%).`;

  if (evaluation.isAmbiguous) {
    const second = evaluation.ranking[1];
    return `${base} Diferencial estrecho con ${second.label}; se sugieren preguntas de desempate.`;
  }

  return base;
}

// ---------------------------------------------------------------------------
// Conexión con el motor de patrones (DiagnosticPatternAnalyzer)
// ---------------------------------------------------------------------------

/** Refuerzo máximo de confianza que la orientación clínica puede aportar. */
const GENIE_MAX_CONFIDENCE_BOOST = 0.05;
/** Tope de confianza tras el refuerzo (la orientación nunca da certeza total). */
const GENIE_CONFIDENCE_CEILING = 0.95;
/** Peso de la evidencia clínica del Genie (menor que datos electrofisiológicos). */
const GENIE_EVIDENCE_WEIGHT = 2;
/** Piso de confianza tras penalización por desempate. */
const TIEBREAKER_CONFIDENCE_FLOOR = 0.05;

/**
 * Construye el objeto compacto de orientación para adjuntar a `ClinicalReport`.
 * Evalúa respuestas de todas las ventanas (síntomas + NCS + EMG).
 */
export function buildGenieOrientation(answers: GenieAnswers): GenieOrientation {
  const evaluation = evaluateGenie(answers);
  return {
    answers: { ...answers },
    topCategory: evaluation.topSuspicion?.category ?? null,
    topLabel: evaluation.topSuspicion?.label ?? null,
    normalized: evaluation.topSuspicion?.normalized ?? 0,
    isAmbiguous: evaluation.isAmbiguous,
  };
}

export interface GenieWarningsOptions {
  liveDiagnoses?: LiveDiagnosis[];
  evaluation?: GenieEvaluation;
}

/** Advertencias clínicas derivadas de respuestas del Genie (guardrails). */
export function getGenieWarnings(
  answers: GenieAnswers,
  options?: GenieWarningsOptions
): string[] {
  const warnings: string[] = [];
  const evaluation = options?.evaluation ?? evaluateGenie(answers);
  const liveIds = new Set((options?.liveDiagnoses ?? []).map(d => d.id));

  if (answers['ncs_temperatura'] === 'no') {
    warnings.push(
      'VCN no confiable: la temperatura cutánea fue inadecuada. Interpretar velocidades con cautela o repetir tras recalentar.'
    );
  }

  for (const rule of GENIE_WARNING_RULES) {
    if (!matchesConditions(answers, rule.when)) continue;
    if (rule.requiresTopMiopatico && evaluation.topSuspicion?.category !== 'miopatico') {
      continue;
    }
    if (rule.requiresLiveGbs && !liveIds.has('gbs')) {
      continue;
    }
    warnings.push(rule.message);
  }

  return warnings;
}

/** ¿El patrón pertenece a la categoría de sospecha indicada? */
export function patternMatchesSuspicion(
  patternId: string,
  category: SuspicionCategory
): boolean {
  return (PATTERN_SUSPICION_CATEGORIES[patternId] ?? []).includes(category);
}

/**
 * Aplica ajustes de confianza según respuestas a desempates (ELA vs MMN, MG vs LEMS).
 */
export function applyTiebreakerAdjustments(
  patterns: DiagnosticPattern[],
  answers: GenieAnswers
): DiagnosticPattern[] {
  const activeTiebreakers = getActiveTiebreakers(patterns.map(p => p.id));
  if (activeTiebreakers.length === 0) return patterns;

  let adjusted = patterns;

  for (const tb of activeTiebreakers) {
    const selectedOptionId = answers[tb.id];
    if (!selectedOptionId) continue;

    const option = tb.options.find(o => o.id === selectedOptionId);
    if (!option?.patternAdjustments) continue;

    adjusted = adjusted.map(pattern => {
      const delta = option.patternAdjustments![pattern.id];
      if (delta === undefined) return pattern;

      const newConfidence = Math.max(
        TIEBREAKER_CONFIDENCE_FLOOR,
        Math.min(GENIE_CONFIDENCE_CEILING, pattern.confidence + delta)
      );

      const evidence: Evidence = {
        source: 'clinical',
        parameter: `genie_tiebreaker_${tb.id}`,
        value: selectedOptionId,
        expectedValue: delta > 0 ? 'compatible' : 'contradictorio',
        weight: Math.abs(delta) >= 0.1 ? 3 : 2,
        description:
          delta > 0
            ? `Desempate "${tb.title}" favorece este patrón: ${option.resolution}`
            : `Desempate "${tb.title}" desfavorece este patrón: ${option.resolution}`,
      };

      return {
        ...pattern,
        confidence: newConfidence,
        supportingEvidence: delta > 0 ? [...pattern.supportingEvidence, evidence] : pattern.supportingEvidence,
        contradictingEvidence: delta < 0 ? [...pattern.contradictingEvidence, evidence] : pattern.contradictingEvidence,
      };
    });
  }

  return adjusted.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Inyecta la orientación del Genie en los patrones detectados como `Evidence`
 * con `source: 'clinical'`. Refuerza patrones compatibles y aplica desempates.
 */
export function applyGenieEvidenceToPatterns(
  patterns: DiagnosticPattern[],
  orientation: GenieOrientation | null | undefined
): DiagnosticPattern[] {
  if (!orientation?.answers || Object.keys(orientation.answers).length === 0) {
    return patterns;
  }

  const answers = orientation.answers;
  let adjusted = patterns;

  if (orientation.topCategory && orientation.normalized > 0) {
    const { topCategory, topLabel, normalized, isAmbiguous } = orientation;
    const boost = GENIE_MAX_CONFIDENCE_BOOST * normalized;
    const pct = Math.round(normalized * 100);

    adjusted = patterns.map(pattern => {
      if (!patternMatchesSuspicion(pattern.id, topCategory)) {
        return pattern;
      }

      const note = isAmbiguous
        ? ' (diferencial estrecho; confirmar con electrofisiología)'
        : '';
      const evidence: Evidence = {
        source: 'clinical',
        parameter: 'genie_topographic_suspicion',
        value: topCategory,
        expectedValue: topCategory,
        weight: GENIE_EVIDENCE_WEIGHT,
        description: `Orientación clínica del Genie compatible: sospecha ${topLabel} (${pct}%)${note}.`,
      };

      return {
        ...pattern,
        confidence: Math.min(GENIE_CONFIDENCE_CEILING, pattern.confidence + boost),
        supportingEvidence: [...pattern.supportingEvidence, evidence],
      };
    });
  }

  return applyTiebreakerAdjustments(adjusted, answers);
}

/**
 * Devuelve los desempates activos según los patrones detectados (ej. ELA vs MMN,
 * MG vs LEMS). Solo aparecen cuando ambos patrones del conflicto están presentes.
 */
export function getActiveTiebreakers(patternIds: string[]): GenieTiebreaker[] {
  const present = new Set(patternIds);
  return GENIE_TIEBREAKERS.filter(t => t.requiresPatterns.every(p => present.has(p)));
}

// ---------------------------------------------------------------------------
// Sugerencias por sección y diagnósticos en vivo
// ---------------------------------------------------------------------------

/**
 * Sugerencias de estudios/maniobras para la ventana actual, según la categoría
 * de sospecha dominante. Devuelve [] si aún no hay sospecha.
 */
export function getGenieSectionSuggestions(
  window: GenieWindow,
  evaluation: GenieEvaluation
): string[] {
  const category = evaluation.topSuspicion?.category;
  if (!category) return [];
  return GENIE_SECTION_SUGGESTIONS[window]?.[category] ?? [];
}

/** Diagnóstico detectado por el motor, en versión compacta para el panel. */
export interface LiveDiagnosis {
  id: string;
  name: string;
  /** Confianza 0–1 del motor. */
  confidence: number;
}

/** Resultado del contraste entre la sospecha inicial del médico y el motor. */
export interface SuspicionComparison {
  /** Sospechas iniciales que el motor también detectó (ids). */
  confirmed: string[];
  /** Sospechas iniciales que el motor aún no respalda (ids). */
  unsupported: string[];
  /** Diagnósticos detectados por el motor que no estaban en la sospecha inicial (ids). */
  unexpected: string[];
  /** El médico marcó explícitamente "sin sospecha inicial". */
  declaredUnknown: boolean;
}

/** Umbral de confianza para considerar que el motor "respalda" un diagnóstico. */
const LIVE_DIAGNOSIS_SUPPORT_THRESHOLD = 0.4;

/**
 * Contrasta la sospecha inicial del médico con los diagnósticos detectados en
 * vivo por el motor. Solo informa (no altera la confianza del análisis).
 */
export function compareSuspicionWithLive(
  initialSuspicions: string[],
  liveDiagnoses: LiveDiagnosis[]
): SuspicionComparison {
  const declaredUnknown = initialSuspicions.includes(UNKNOWN_SUSPICION_ID);
  const initial = initialSuspicions.filter(id => id !== UNKNOWN_SUSPICION_ID);
  const supportedIds = new Set(
    liveDiagnoses
      .filter(d => d.confidence >= LIVE_DIAGNOSIS_SUPPORT_THRESHOLD)
      .map(d => d.id)
  );

  const confirmed = initial.filter(id => supportedIds.has(id));
  const unsupported = initial.filter(id => !supportedIds.has(id));
  const unexpected = [...supportedIds].filter(id => !initial.includes(id));

  return { confirmed, unsupported, unexpected, declaredUnknown };
}

export {
  inferGenieAnswersFromClinicalText,
  inferGenieAnswersFromClinicalSources,
} from './clinicalSymptomsExtractor';

/** Prefiere respuestas NCS del Genie a partir de registros extraídos o confirmados. */
export function inferGenieAnswersFromNcs(
  ncsResults: Array<{
    type?: string;
    status?: string;
    amplitude?: number;
    absentResponse?: boolean;
    findings?: string[];
  }>
): GenieAnswers {
  if (!ncsResults.length) return {};

  const answers: GenieAnswers = {};
  const sensory = ncsResults.filter(result => result.type === 'sensory');

  if (sensory.length > 0) {
    const snapReduced = sensory.some(result =>
      result.absentResponse ||
      result.amplitude === 0 ||
      result.status === 'abnormal' ||
      result.findings?.some(finding => /amplitud reducida|snap.*ausente|severamente reducido/i.test(finding))
    );
    answers.ncs_snap = snapReduced ? 'disminuidos' : 'normales';
  }

  const anyAbnormal = ncsResults.some(result => result.status === 'abnormal');
  const demyelinating = ncsResults.some(result =>
    result.findings?.some(finding =>
      /velocidad de conduccion lenta|latencia prolongada|bloqueo|desmielin/i.test(
        finding.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      )
    )
  );

  if (!anyAbnormal) {
    answers.ncs_vcn_patron = 'normal';
  } else if (demyelinating) {
    answers.ncs_vcn_patron = 'desmielinizante';
  } else {
    answers.ncs_vcn_patron = 'axonal';
  }

  return answers;
}

/** Reexporta el helper de nombres para consumidores del motor. */
export { getDiagnosisName };
